/**
 * KYC ADMIN MANAGEMENT SERVICE
 *
 * Comprehensive service for managing KYC submissions in the admin dashboard.
 * Handles fetching, filtering, searching, status updates, and audit logging.
 *
 * Enhanced with document management capabilities for viewing and managing
 * uploaded KYC documents including ID cards, proof of address, and selfies.
 *
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */

import { kycNotificationService } from './kycNotificationService';
import { notificationService } from '../notificationService';
import { ensureSessionHeaders } from '../serverSession';

// =============================================================================
// API HELPER - All DB access goes through the server-side API
// =============================================================================
async function callKYCAdminAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const sessionHeaders = await ensureSessionHeaders();
 const response = await fetch('/api/admin/kyc', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({ operation, data })
 });
 const result = await response.json();
 if (!response.ok) {
 throw new Error(result.error || `KYC Admin API error: ${response.status}`);
 }
 return result.data !== undefined ? result.data : result;
}

// Document interfaces
export interface KYCDocument {
 id: string;
 kyc_id: string;
 user_id: number;
 // Must match DB CHECK constraint on kyc_documents.document_type
 document_type: 'id_document' | 'proof_of_address' | 'selfie' | 'additional' | 'company_registration';
 document_category?: 'identity_verification' | 'address_verification' | 'biometric_verification' | 'supplementary';
 file_name: string;
 file_path: string;
 storage_bucket: string;
 file_size_bytes?: number;
 mime_type?: string;
 file_hash?: string;
 verification_status: 'pending' | 'approved' | 'rejected' | 'requires_resubmission' | 'expired';
 quality_score?: number;
 confidence_score?: number;
 reviewed_by?: string;
 reviewed_at?: string;
 rejection_reason?: string;
 admin_notes?: string;
 metadata?: any;
 uploaded_at: string;
 created_at: string;
 updated_at: string;
}

export interface DocumentAccessLog {
 id: string;
 document_id: string;
 user_id?: number;
 access_type: 'view' | 'download' | 'approve' | 'reject';
 ip_address?: string;
 user_agent?: string;
 accessed_at: string;
}

export interface KYCSubmission {
 id: string;
 user_id: number;
 first_name: string;
 last_name: string;
 full_legal_name: string;
 id_type: 'national_id' | 'passport' | 'drivers_license';
 id_number_encrypted: string;
 id_number_hash?: string;
 /** Alias of id_number_hash. There is no unique_id_hash column. */
 unique_id_hash?: string | null;
 phone_number: string;
 email_address: string;
 street_address: string;
 city: string;
 postal_code: string;
 country_code: string;
 country_name: string;
 data_consent_given: boolean;
 privacy_policy_accepted: boolean;
 kyc_status: 'pending' | 'completed' | 'rejected' | 'expired';
 kyc_completed_at: string;
 certificate_requested: boolean;
 certificate_generated_at?: string;
 certificate_sent_at?: string;
 created_at: string;
 updated_at: string;
 created_by_telegram_id?: number;
 last_modified_by?: string;
 rejection_reason?: string; // Added for rejection feedback
 kyc_editable?: boolean; // When true, user can edit their KYC even if status is completed
 // Company purchase fields
 is_company_purchase?: boolean;
 company_name?: string;
 company_registration_number?: string;
 company_registration_document_url?: string;
 company_registration_document_filename?: string;
 // Associated documents
 documents?: KYCDocument[];
 // User information
 users?: {
 id: number;
 username: string;
 email: string;
 full_name: string;
 created_at: string;
 is_active: boolean;
 };
}

export interface KYCStatistics {
 total: number;
 pending: number;
 completed: number;
 rejected: number;
 expired: number;
 averageProcessingTime: number; // in hours
 completionRate: number; // percentage
 recentSubmissions: number; // last 7 days
}

export interface KYCFilters {
 status?: 'all' | 'pending' | 'completed' | 'rejected' | 'expired';
 dateRange?: {
 start: string;
 end: string;
 };
 country?: string;
 searchTerm?: string;
}

export interface KYCStatusUpdateData {
 kycId: string;
 newStatus: 'completed' | 'rejected';
 adminEmail: string;
 comments?: string;
 rejectionReason?: string;
}

export class KYCAdminService {

 /**
 * Get all KYC submissions with optional filtering
 */
 async getKYCSubmissions(
 filters: KYCFilters = {},
 limit: number = 50,
 offset: number = 0
 ): Promise<{ submissions: KYCSubmission[]; total: number }> {
 try {
 console.log('Fetching KYC submissions with filters:', filters);
 return await callKYCAdminAPI('get_submissions', { filters, limit, offset });
 } catch (error) {
 console.error('Error in getKYCSubmissions:', error);
 throw error;
 }
 }

 /**
 * Get KYC statistics for dashboard overview
 */
 async getKYCStatistics(): Promise<KYCStatistics> {
 try {
 console.log('Calculating KYC statistics...');
 const stats = await callKYCAdminAPI('get_statistics');
 console.log('KYC statistics calculated:', stats);
 return stats;
 } catch (error) {
 console.error('Error calculating KYC statistics:', error);
 return { total: 0, pending: 0, completed: 0, rejected: 0, expired: 0, averageProcessingTime: 0, completionRate: 0, recentSubmissions: 0 };
 }
 }

 /**
 * Get a single KYC submission by ID
 */
 async getKYCSubmissionById(kycId: string): Promise<KYCSubmission | null> {
 try {
 console.log(`Fetching KYC submission: ${kycId}`);
 const submission = await callKYCAdminAPI('get_submission_by_id', { kycId });
 if (!submission) { console.log('ℹ KYC submission not found'); return null; }
 console.log('KYC submission fetched successfully');
 return submission;
 } catch (error) {
 console.error('Error in getKYCSubmissionById:', error);
 throw error;
 }
 }

 /**
 * Quick approve KYC submission (for inline actions)
 */
 async quickApproveKYC(kycId: string, adminEmail: string, comments?: string): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Quick approving KYC: ${kycId}`);
 const result = await callKYCAdminAPI('approve_kyc', { kycId, adminEmail, comments });

 // Send email notification
 if (result.userId) {
 const notificationResult = await kycNotificationService.sendKYCStatusNotification({
 kycId, userId: result.userId, status: 'approved', comments, adminEmail
 });
 if (!notificationResult.success) console.warn('Failed to send KYC notification email:', notificationResult.error);

 // Create in-app notification
 await notificationService.createNotification({
 user_id: result.userId, type: 'account_update',
 title: ' KYC Verification Approved!',
 message: `Congratulations! Your KYC verification has been approved. You can now purchase shares and access all platform features.${comments ? ` Admin note: ${comments}` : ''}`,
 action_url: '/dashboard',
 metadata: { kyc_id: kycId, admin_email: adminEmail, approval_date: new Date().toISOString() }
 });
 }

 console.log(`KYC quick approved successfully: ${kycId}`);
 return { success: true };
 } catch (error) {
 console.error('Error in quickApproveKYC:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Quick reject KYC submission (for inline actions)
 */
 async quickRejectKYC(kycId: string, adminEmail: string, rejectionReason: string, comments?: string): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Quick rejecting KYC: ${kycId}`);
 if (!rejectionReason.trim()) return { success: false, error: 'Rejection reason is required' };

 const result = await callKYCAdminAPI('reject_kyc', { kycId, adminEmail, rejectionReason, comments });

 // Send email notification
 if (result.userId) {
 const notificationResult = await kycNotificationService.sendKYCStatusNotification({
 kycId, userId: result.userId, status: 'rejected', rejectionReason, comments, adminEmail
 });
 if (!notificationResult.success) console.warn('Failed to send KYC notification email:', notificationResult.error);

 // Create in-app notification
 await notificationService.createNotification({
 user_id: result.userId, type: 'account_update',
 title: ' KYC Verification Requires Attention',
 message: `Your KYC verification was not approved. Reason: ${rejectionReason}. Please review and resubmit your documents.${comments ? ` Admin note: ${comments}` : ''}`,
 action_url: '/kyc',
 metadata: { kyc_id: kycId, admin_email: adminEmail, rejection_reason: rejectionReason, rejection_date: new Date().toISOString() }
 });
 }

 console.log(`KYC quick rejected successfully: ${kycId}`);
 return { success: true };
 } catch (error) {
 console.error('Error in quickRejectKYC:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Approve KYC submission (legacy method)
 */
 async approveKYC(kycId: string, adminEmail: string, comments?: string): Promise<{ success: boolean; error?: string }> {
 return this.quickApproveKYC(kycId, adminEmail, comments);
 }

 /**
 * Reject KYC submission (legacy method)
 */
 async rejectKYC(kycId: string, adminEmail: string, rejectionReason: string, comments?: string): Promise<{ success: boolean; error?: string }> {
 return this.quickRejectKYC(kycId, adminEmail, rejectionReason, comments);
 }

 /**
 * Unlock KYC for user editing - allows user to edit their KYC even if it's completed
 */
 async unlockKYCForEditing(kycId: string, adminEmail: string, unlock: boolean = true): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`${unlock ? 'Unlocking' : 'Locking'} KYC for editing: ${kycId}`);
 await callKYCAdminAPI('unlock_kyc', { kycId, adminEmail, unlock });
 console.log(`KYC ${unlock ? 'unlocked' : 'locked'} successfully`);
 return { success: true };
 } catch (error) {
 console.error('Error in unlockKYCForEditing:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Update KYC status (approve or reject)
 */
 async updateKYCStatus(updateData: KYCStatusUpdateData): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Updating KYC status: ${updateData.kycId} -> ${updateData.newStatus}`);

 if (updateData.newStatus === 'completed') {
 return this.quickApproveKYC(updateData.kycId, updateData.adminEmail, updateData.comments);
 } else if (updateData.newStatus === 'rejected') {
 return this.quickRejectKYC(updateData.kycId, updateData.adminEmail, updateData.rejectionReason || 'No reason provided', updateData.comments);
 }

 // For other statuses, use approve_kyc API with the status
 await callKYCAdminAPI('approve_kyc', {
 kycId: updateData.kycId,
 adminEmail: updateData.adminEmail,
 comments: updateData.comments,
 newStatus: updateData.newStatus
 });

 console.log(`KYC status updated successfully: ${updateData.newStatus}`);
 return { success: true };
 } catch (error) {
 console.error('Error in updateKYCStatus:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Get available countries from KYC submissions for filtering
 */
 async getAvailableCountries(): Promise<Array<{ code: string; name: string; count: number }>> {
 try {
 console.log('Fetching available countries...');
 const result = await callKYCAdminAPI('get_countries');
 console.log(`Found ${result.length} countries`);
 return result;
 } catch (error) {
 console.error('Error in getAvailableCountries:', error);
 return [];
 }
 }

 /**
 * Batch approve multiple KYC submissions
 */
 async batchApproveKYC(kycIds: string[], adminEmail: string, comments?: string): Promise<{
 successful: number;
 failed: number;
 errors: Array<{ kycId: string; error: string }>;
 }> {
 console.log(`Batch approving ${kycIds.length} KYC submissions...`);

 let successful = 0;
 let failed = 0;
 const errors: Array<{ kycId: string; error: string }> = [];

 for (const kycId of kycIds) {
 try {
 const result = await this.approveKYC(kycId, adminEmail, comments);
 if (result.success) {
 successful++;
 } else {
 failed++;
 errors.push({ kycId, error: result.error || 'Unknown error' });
 }
 } catch (error) {
 failed++;
 errors.push({
 kycId,
 error: error instanceof Error ? error.message : 'Unknown error'
 });
 }

 // Add small delay to prevent overwhelming the system
 await new Promise(resolve => setTimeout(resolve, 100));
 }

 console.log(`Batch approval completed: ${successful} successful, ${failed} failed`);
 return { successful, failed, errors };
 }

 /**
 * Batch reject multiple KYC submissions
 */
 async batchRejectKYC(
 rejections: Array<{ kycId: string; rejectionReason: string }>,
 adminEmail: string,
 comments?: string
 ): Promise<{
 successful: number;
 failed: number;
 errors: Array<{ kycId: string; error: string }>;
 }> {
 console.log(`Batch rejecting ${rejections.length} KYC submissions...`);

 let successful = 0;
 let failed = 0;
 const errors: Array<{ kycId: string; error: string }> = [];

 for (const rejection of rejections) {
 try {
 const result = await this.rejectKYC(
 rejection.kycId,
 adminEmail,
 rejection.rejectionReason,
 comments
 );
 if (result.success) {
 successful++;
 } else {
 failed++;
 errors.push({ kycId: rejection.kycId, error: result.error || 'Unknown error' });
 }
 } catch (error) {
 failed++;
 errors.push({
 kycId: rejection.kycId,
 error: error instanceof Error ? error.message : 'Unknown error'
 });
 }

 // Add small delay to prevent overwhelming the system
 await new Promise(resolve => setTimeout(resolve, 100));
 }

 console.log(`Batch rejection completed: ${successful} successful, ${failed} failed`);
 return { successful, failed, errors };
 }

 /**
 * Get KYC submissions that need review (pending status)
 */
 async getPendingKYCSubmissions(limit: number = 20): Promise<KYCSubmission[]> {
 const result = await this.getKYCSubmissions(
 { status: 'pending' },
 limit,
 0
 );
 return result.submissions;
 }

 /**
 * Search KYC submissions by user information
 */
 async searchKYCSubmissions(searchTerm: string, limit: number = 20): Promise<KYCSubmission[]> {
 const result = await this.getKYCSubmissions(
 { searchTerm },
 limit,
 0
 );
 return result.submissions;
 }

 /**
 * Get KYC submission history for a specific user
 */
 async getUserKYCHistory(userId: number): Promise<KYCSubmission[]> {
 try {
 console.log(`Fetching KYC history for user: ${userId}`);
 const submissions = await callKYCAdminAPI('get_user_kyc_history', { userId });
 console.log(`Found ${submissions?.length || 0} KYC submissions for user`);
 return submissions || [];
 } catch (error) {
 console.error('Error in getUserKYCHistory:', error);
 return [];
 }
 }

 /**
 * Get documents for a specific KYC submission
 */
 async getKYCDocuments(kycId: string): Promise<KYCDocument[]> {
 try {
 console.log(`Fetching documents for KYC: ${kycId}`);
 const documents = await callKYCAdminAPI('get_documents', { kycId });
 console.log(`Found ${documents?.length || 0} documents for KYC`);
 return documents || [];
 } catch (error) {
 console.error('Error in getKYCDocuments:', error);
 return [];
 }
 }

 /**
 * Get secure signed URL for document access
 */
 async getDocumentSignedUrl(documentId: string, expiresIn: number = 3600): Promise<{ url?: string; error?: string }> {
 try {
 console.log(`Generating signed URL for document: ${documentId}`);
 const result = await callKYCAdminAPI('get_signed_url', { documentId, expiresIn });
 console.log('Signed URL generated successfully');
 return { url: result.url };
 } catch (error) {
 console.error('Error in getDocumentSignedUrl:', error);
 return { error: 'Failed to generate secure URL' };
 }
 }

 /**
 * Log document access for audit trail
 */
 async logDocumentAccess(
 documentId: string,
 accessType: 'view' | 'download' | 'approve' | 'reject',
 userId?: number,
 ipAddress?: string,
 userAgent?: string
 ): Promise<void> {
 try {
 console.log(`Logging document access: ${documentId} - ${accessType}`);
 await callKYCAdminAPI('log_access', { documentId, accessType, userId, ipAddress, userAgent });
 console.log('Document access logged successfully');
 } catch (error) {
 console.error('Error in logDocumentAccess:', error);
 }
 }

 /**
 * Update individual field approval status
 */
 async updateFieldApproval(
 kycId: string,
 fieldName: string,
 status: 'approved' | 'rejected',
 adminEmail: string,
 adminNotes?: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Updating field approval: ${kycId} -> ${fieldName} -> ${status}`);
 await callKYCAdminAPI('update_field_approval', { kycId, fieldName, status, adminEmail, adminNotes });
 console.log(`Field approval updated successfully: ${fieldName} -> ${status}`);
 return { success: true };
 } catch (error) {
 console.error('Error in updateFieldApproval:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Get field approvals for a KYC submission
 */
 async getFieldApprovals(kycId: string): Promise<Record<string, string>> {
 try {
 console.log(`Loading field approvals for KYC: ${kycId}`);
 const approvals = await callKYCAdminAPI('get_field_approvals', { kycId });
 console.log(`Loaded ${Object.keys(approvals).length} field approvals`);
 return approvals;
 } catch (error) {
 console.warn('Could not load field approvals:', error);
 return {};
 }
 }

 /**
 * Update document verification status
 */
 async updateDocumentStatus(
 documentId: string,
 status: 'approved' | 'rejected' | 'requires_resubmission',
 adminEmail: string,
 rejectionReason?: string,
 adminNotes?: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Updating document status: ${documentId} -> ${status}`);
 await callKYCAdminAPI('update_document_status', { documentId, status, adminEmail, rejectionReason, adminNotes });
 console.log('Document status updated successfully');
 return { success: true };
 } catch (error) {
 console.error('Error in updateDocumentStatus:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Get document access history for audit
 */
 async getDocumentAccessHistory(documentId: string): Promise<DocumentAccessLog[]> {
 try {
 console.log(`Fetching access history for document: ${documentId}`);
 const accessLogs = await callKYCAdminAPI('get_access_history', { documentId });
 console.log(`Found ${accessLogs?.length || 0} access log entries`);
 return accessLogs || [];
 } catch (error) {
 console.error('Error in getDocumentAccessHistory:', error);
 return [];
 }
 }

 /**
 * Enhanced method to get KYC submissions with documents
 */
 async getKYCSubmissionsWithDocuments(
 filters: KYCFilters = {},
 limit: number = 50,
 offset: number = 0
 ): Promise<{ submissions: KYCSubmission[]; total: number }> {
 try {
 // First get the basic submissions
 const result = await this.getKYCSubmissions(filters, limit, offset);

 // Then fetch documents for each submission
 const submissionsWithDocuments = await Promise.all(
 result.submissions.map(async (submission) => {
 const documents = await this.getKYCDocuments(submission.id);
 return {
 ...submission,
 documents
 };
 })
 );

 return {
 submissions: submissionsWithDocuments,
 total: result.total
 };

 } catch (error) {
 console.error('Error in getKYCSubmissionsWithDocuments:', error);
 return { submissions: [], total: 0 };
 }
 }

 /**
 * Delete a rejected KYC submission and all associated data
 * Allows user to resubmit from scratch
 */
 async deleteRejectedKYC(
 kycId: string,
 adminId: number,
 adminEmail: string
 ): Promise<{ success: boolean; message: string; userId?: number }> {
 try {
 console.log(`Starting KYC deletion for KYC ID: ${kycId}`);
 const result = await callKYCAdminAPI('delete_rejected_kyc', { kycId, adminId, adminEmail });
 console.log(`KYC deletion completed for user ${result.userId}`);
 return { success: true, message: 'KYC submission deleted successfully', userId: result.userId };
 } catch (error) {
 console.error('Error in deleteRejectedKYC:', error);
 return { success: false, message: error instanceof Error ? error.message : 'Failed to delete KYC' };
 }
 }
}

// Export singleton instance
export const kycAdminService = new KYCAdminService();
